const getLocalePostfix = (locale: string) =>
  locale !== "ar" ? `/${locale}` : "";

export const getCurrentPath = () => {
  if (typeof window !== "undefined") {
    return window.location.href;
  }
  return "";
};

export const getWindowOrigin = (locale: string) => {
  if (typeof window !== "undefined") {
    return `${window.location.origin}${getLocalePostfix(locale)}`;
  }
  return "";
};

/**
 * Navigate programmatically to an external url. we will try to open
 * the url in a new tab and if it doesn't work due to pop-ups being blocked,
 * we will open the url in the current tab.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/open#return_value
 *
 * @param {string} url
 */
export const navigateToExternalUrl = (url: string) => {
  if (typeof window !== "undefined") {
    // if it's being blocked
    if (!window.open(url, "_blank")) {
      window.location.replace(url);
    }
  }
};

/**
 * Get the base path of the current deployment on Vercel/local machine
 * e.g. http://localhost
 * if we want to construct a full path e.g. when we add alternate languages
 * meta tags.
 *
 * @see https://vercel.com/docs/concepts/projects/environment-variables
 * @returns {string}
 */
export const getBasePath = (): string =>
  `${
    process.env.NEXT_PUBLIC_VERCEL_ENV === "development" ? "https" : "https"
  }://${process.env.NEXT_PUBLIC_VERCEL_URL}`;

/**
 * Determines whether a given navigation link should be marked as active.
 *
 * @param {string} pathname - The current URL path from Next.js router.
 * @param {string} [href] - The href of the navigation link.
 * @returns {boolean} - Returns `true` if the link should be active, otherwise `false`.
 *
 * Logic:
 * - If `href` is undefined or null, return `false` (prevents accidental activation).
 * - If `href` is `/`, the link is active only when `pathname` is exactly `/`.
 * - Otherwise, checks if `pathname` starts with `href` to determine activation.
 */
export const matchPath = (pathname: string, href?: string | null): boolean => {
  if (!href) return false; // Prevents unintended activation when href is undefined or null

  // Special case for home page
  if (href === "/") {
    return pathname === "/";
  }

  // For other routes, check if pathname starts with href
  return pathname.startsWith(href);
};

/**
 * Checks if a given string is a valid URL.
 *
 * This function validates whether the input is a well-formed URL,
 * considering both absolute and relative URLs.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} Returns `true` if the string is a valid URL, otherwise `false`.
 *
 * @example
 * isUrl('https://example.com'); // true
 * isUrl('http://localhost:3000'); // true
 * isUrl('ftp://files.server.com'); // true
 * isUrl('/path/to/resource'); // false
 * isUrl('not a url'); // false
 */
export function isUrl(str: string): boolean {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * Builds a URL with query parameters appended.
 *
 * @param url - The base URL (e.g. "https://example.com/page")
 * @param params - An object representing query parameters to append
 * @returns The full URL with serialized query parameters
 *
 * @example
 * buildUrl("https://example.com", { page: 2, search: "query" });
 * // Returns: "https://example.com?page=2&search=query"
 *
 * @note
 * - This function only supports flat objects (no nested or array values).
 * - `undefined` and `null` values are converted to the string `"undefined"` and `"null"`.
 *   You may want to pre-filter the object if you want to exclude them.
 */
export const buildUrl = (
  url: string,
  searchParams: Record<string, any>
): string => {
  const urlSearchParams = new URLSearchParams(searchParams);
  const queryString = urlSearchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};
